import { Link, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';

import Dialog from 'modules/common/components/MUI/Dialog';
import useCompleteMemberTask from 'modules/Tasks/hooks/useCompleteMemberTask';
import { SubmitAction, SubmitActionTask, SubmitActionTypes } from 'modules/Tasks/types';
import interpolate from 'modules/Tasks/utils/interpolate';

const DEFAULT_COMPLETE_LABEL = 'Mark as done';

const STATE_LIST = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/**
 * The fields this dialog reads beyond `SubmitActionTask`, all optional: the registry hands every
 * custom component the narrow type, and the member-task payload carries these without the type
 * saying so. Each is checked before use rather than cast.
 */
interface FileNewHireReportTask extends SubmitActionTask {
    title?: string | null;
    due_date?: string | null;
    task_template: SubmitActionTask['task_template'] & {
        instructions?: string;
        submit_action?: SubmitAction;
    };
}

interface FileNewHireReportModalProps {
    open: boolean;
    onClose: () => void;
    task: FileNewHireReportTask;
}

const stringProp = (props: Record<string, unknown> | undefined, key: string): string | null => {
    const value = props?.[key];

    return typeof value === 'string' && value.trim() ? value : null;
};

const statesOf = (context: unknown): string[] => {
    const states = (context as { states?: unknown } | null)?.states;

    return Array.isArray(states) ? states.filter((state): state is string => typeof state === 'string') : [];
};

/** The context as the template's `{placeholder}` values, or none when it is not an object. */
const contextOf = (context: unknown): Record<string, unknown> =>
    typeof context === 'object' && context !== null ? (context as Record<string, unknown>) : {};

/**
 * The member's own to-do for a new hire report they chose to file themselves, when confirming an
 * employee or contractor. Everything it says comes from the task: the per-hire title (the template's
 * name filled from the context, as the member task list sends no title), the template's instructions,
 * the states on its context, its due date and the guide the template links — so the filing copy
 * lives with the template, in one place.
 *
 * The member's word is taken for it: one click marks the task done, with no upload or proof, as for
 * the other member-completed filing to-dos. `task.description` is deliberately not rendered — it
 * restates the instructions almost word for word.
 */
export default function FileNewHireReportModal({ open, onClose, task }: Readonly<FileNewHireReportModalProps>) {
    const completeTask = useCompleteMemberTask(task, onClose);
    const { enqueueSnackbar } = useSnackbar();
    const [isCompleting, setIsCompleting] = useState(false);

    const submitAction = task.task_template.submit_action;
    const componentProps = submitAction?.type === SubmitActionTypes.CUSTOM ? submitAction.component_props : undefined;
    const helpUrl = stringProp(componentProps, 'help_url');
    const completeLabel = stringProp(componentProps, 'complete_label') ?? DEFAULT_COMPLETE_LABEL;
    const states = statesOf(task.context);

    const handleComplete = useCallback(() => {
        setIsCompleting(true);
        completeTask({
            trackingEvent: 'New Hire Report Task Completed',
            onSuccess: () => setIsCompleting(false),
            onError: () => {
                setIsCompleting(false);
                enqueueSnackbar("We couldn't mark this task as done. Please try again.", { variant: 'error' });
            },
        });
    }, [completeTask, enqueueSnackbar]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            heading={task.title || interpolate(task.task_template.display_name, contextOf(task.context))}
            size="medium"
            positiveAction={{ label: completeLabel, onClick: handleComplete, loading: isCompleting }}>
            <Stack spacing={2}>
                {task.task_template.instructions && (
                    <Typography variant="body1" color="text.secondary">
                        {task.task_template.instructions}
                    </Typography>
                )}
                {(states.length > 0 || task.due_date) && (
                    <Stack spacing={0.5}>
                        {states.length > 0 && (
                            <Typography variant="body2">{`${
                                states.length > 1 ? 'States' : 'State'
                            }: ${STATE_LIST.format(states)}`}</Typography>
                        )}
                        {/* Local time and month/day, as the task card's date badge renders the same date. */}
                        {task.due_date && (
                            <Typography variant="body2">{`Due: ${dayjs(task.due_date).format(
                                'MMM D, YYYY'
                            )}`}</Typography>
                        )}
                    </Stack>
                )}
                {helpUrl && (
                    <Link href={helpUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                        Learn how new hire reporting works
                    </Link>
                )}
            </Stack>
        </Dialog>
    );
}
